import { ReactElement, useEffect, useState } from 'react';
import { Box, Typography, Backdrop, CircularProgress } from '@mui/material';
import PolicyList from './components/policy/PolicyList';
import AiSolution from './components/aisolution/AiSolution';
import Dataset from './components/dataset/Dataset';
import FeedbackForm from './components/feedback/FeedbackForm';
import BusinessUsageInformation from './components/businessusage/BusinessUsageInformation';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AppFormikButtonTiles } from 'shared/components/AppComponents';
import { Formik } from 'formik';
import { PublicService } from 'services/PublicService';
import { PublicActions } from 'state/public/Action';
import { Utils } from 'shared/helpers';

export interface AISolutionProps {
  _id: string;
  sourceType: string;
  classification: string;
  solutionName: string;
  solutionOwner: string;
  organizationUnit: string;
  organizationSubUnit: string;
  stakeholder: string;
  legalEntity: string;
  userCustomerName: string;
  businessProcesses: string;
  uses: string;
  purpose: string;
  businessGoals: string;
  businessValue: string;
  platformName: string;
  riskTier: string;
  openSource: boolean;
  aiModelTypes: any[];
  technicalDocumentationLink: string;
  vendorName: string;
  consumptionMethod: string;
  environment: string;
  watermarkingTechnique: string;
  lifecycleStage: string;
  versionControlMethod: string;
  decisionCapability: string;
  buildDate: any;
  lastTrainingDate: any;
  approvalStatus: string;
  organizationId: string;
  datasetId: any[];
  tenantId: any;
  createdBy: any;
  updatedBy: any;
  updatedDate: any;
  isActive: boolean;
  deletedAt: any;
  createdDate: string;
  __v: number;
}

const PublicCommandPortal = (props: any): ReactElement => {
  const { setLoginData, publicState } = props;
  const { orgName } = publicState;
  const { tokenId } = useParams<{ tokenId: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [aiSolutionList, setAISolutionList] = useState<AISolutionProps[]>([]);

  const getAILists = (): void => {
    PublicService.getAiSystem('approved')
      .then((res) => {
        const { data } = res.data;
        setAISolutionList(data);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  const fetchAccessToken = (): void => {
    setIsLoading(true);
    PublicService.getAccessToken(tokenId!)
      .then((res) => {
        const { accessToken, refreshToken, orgName } = res.data.data;
        setLoginData({
          accessToken,
          refreshToken,
          isUserLoggedIn: true,
          orgName,
        });
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setIsLoading(false);
        getAILists();
      });
  };

  useEffect(() => {
    if (tokenId) {
      fetchAccessToken();
    }
  }, [setLoginData, tokenId]);

  if (isLoading) {
    return (
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress color="inherit" />
          <Typography sx={{ mt: 2, color: 'white' }}>
            Validating submission token...
          </Typography>
        </Box>
      </Backdrop>
    );
  }

  return (
    <Box className="public-container">
      {/* HEADER */}

      <Box className="public-header-card public-card">
        <Box className="public-header-title">
          <Typography variant="h2">Clarity AI</Typography>
        </Box>

        <Typography className="header-subtext">
          Submitting to: <span>{orgName}</span>
        </Typography>
      </Box>

      {/* TABS */}
      <Box className="public-tabs">
        <Formik
          initialValues={{ status: [true, false] }}
          onSubmit={() => console.log('test')}
        >
          {({ values }) => (
            <>
              <AppFormikButtonTiles
                variant="outlined"
                name="status"
                size="small"
                label=""
                disabled={false}
                multichoice={false}
                className="app-group-button mb-5"
                buttons={[
                  {
                    value: 'Policies',
                  },
                  {
                    value: 'Approved AI Solutions',
                  },
                  {
                    value: 'Approved Datasets',
                  },
                  {
                    value: '+ Submit New AI',
                  },
                  {
                    value: 'Provide AI Feedback',
                  },
                ]}
                outerLabel
              />
              {values.status[0] && <PolicyList />}
              {values.status[1] && (
                <Box>
                  <AiSolution
                    aiSolutionList={aiSolutionList}
                    setAISolutionList={setAISolutionList}
                  />
                </Box>
              )}

              {values.status[2] && <Dataset />}

              {values.status[3] && <BusinessUsageInformation />}

              {values.status[4] && (
                <FeedbackForm
                  aiSolutions={aiSolutionList
                    ?.filter((item) => item._id && item.solutionName) // optional filter
                    .map((item) => ({
                      aisystemId: item._id,
                      solutionName: item.solutionName,
                    }))}
                />
              )}
            </>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

const mapDispatchToProps = (dispatch: any): any => ({
  setLoginData: (data: any) => dispatch(PublicActions.setLoginStatus(data)),
});

const mapStateToProps = (state: any): any => ({
  publicState: state.publicState,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PublicCommandPortal);
